<template>
  <div :class="`theme-form--dark credit-app-page credit-app-page--${status}`">
    <div class="card">
      <header class="card__header">
        <h2>Review Credit Application</h2>
      </header>

      <div class="card__body">
        <section class="row">
          <div class="col-md-12">
            <SaxxText :form="formName" name="ccode">Customer Code</SaxxText>
          </div>
        </section>
      </div>

      <ApplicationForm
          :lang="lang"
          :formName="formName"
          :additionalFields="additionalFields"
          @submit="update">
        <template v-slot:certImage>
          <div style="display: flex; flex-wrap: wrap; justify-content: space-between;">
            <a v-for="img in images" :key="img.id" :href="img.absolute_url" target="_blank">
              <img v-if="isImage(img.absolute_url)" :src="img.absolute_url" :alt="`${img.type}`"
                   style="display: block; max-width: 25rem;"/>
              <template v-else>{{ img.absolute_url }}</template>
            </a>
          </div>
        </template>

        <div class="row" v-if="account_id">
          <div class="col-md-12">
            <p>
              <router-link :to="`/credit-dashboard/accounts/${account_id}`">Goto existing credit account:
                {{ account_name }}
              </router-link>
            </p>
          </div>
        </div>

        <div class="row">
          <div v-if="status !== 'pending'" class="col-md-12">
            <p>
              This credit application was
              <strong>{{ status }}</strong> on
              <strong>{{ updated_at }}</strong> by
              <strong>{{ actioned_by_name }}</strong>.
            </p>
          </div>

          <div class="col-md-12">
            <button class="btn btn-dark" type="submit">Update</button>
            <button class="btn btn-dark" type="button" @click="resend">Re-send email</button>
            <button class="btn btn-default" type="button" @click="resendD365">Re-send to D365</button>
            <button
                v-if="status !== 'declined'"
                class="btn btn-danger"
                type="button"
                @click="() => review('declined')"
                :disabled="isLoading"
            >Decline
            </button>
            <button
                v-if="status !== 'approved'"
                class="btn btn-success"
                type="button"
                @click="() => review('approved')"
                :disabled="isLoading"
            >Approve
            </button>
          </div>
        </div>
      </ApplicationForm>
    </div>
  </div>
</template>

<script>
import axios, {RepositoryError} from "../../repositories";
import {mapActions, mapGetters} from "vuex";
import {isImage, notify} from "../../utils";

import SaxxText from '../forms/SaxxText';
import ApplicationForm from './ApplicationForm';

export default {
  name: "ApplicationReview",
  components: {
    SaxxText,
    ApplicationForm,
  },
  mounted() {
    this.fetchCreditApplication(this.id);
  },
  props: {
    id: {
      required: true,
      type: String | Number,
    },
    lang: {
      required: false,
      type: String,
      default: 'en',
    },
  },
  data() {
    return {
      formName: "b2b.new-applications.review",
      status: "pending",
      updated_at: "",
      images: [],
      account_id: null,
      account_name: null,
      actioned_by_name: null,
      additionalFields: {
        ccode: "",
      },
    };
  },
  methods: {
    ...mapActions("forms", [
      "setStatus",
      "setErrors",
      "setFields",
    ]),
    async resendD365() {
      try {
        await axios.put(`/credit-dashboard/applications/${this.id}/resendD365`);

        notify(
            "Resend customer records to D365",
            "success"
        );
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }
        throw err;
      }
    },
    async resend() {
      try {
        await axios.put(`/credit-dashboard/applications/${this.id}/email`);

        notify(
            "Email re-send request has been received. The email should be re-sent in a few moments.",
            "success"
        );
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      }
    },
    async update(fields) {
      try {
        this.setStatus({
          form: this.formName,
          status: "loading",
        });

        await axios.put(`/credit-dashboard/applications/${this.id}`, {
          ...fields,
          sameInfoBuyer: Number(fields.sameInfoBuyer),
          sameInfoForRep: Number(fields.sameInfoForRep),
          everBankruptcy: Number(fields.everBankruptcy),
          differentShippingAddress: Number(fields.differentShippingAddress),
          sameEmailForConfirmation: Number(fields.sameEmailForConfirmation),
          agreeToTermsAndConditions: Number(fields.agreeToTermsAndConditions),
        });

        notify("Application details successfully updated", "success");
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      } finally {
        this.setStatus({
          form: this.formName,
          status: "idle",
        });
      }
    },
    async review(verdict) {
      try {
        this.setStatus({
          form: this.formName,
          status: "loading",
        });

        const response = await axios.put(
            `/credit-dashboard/applications/${this.id}/review`,
            {
              verdict
            }
        );

        this.status = verdict;
        this.updated_at = new Date().toLocaleString();
        notify(`This credit application has been ${verdict}`, "success");
        this.$router.back();
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      } finally {
        this.setStatus({
          form: this.formName,
          status: "idle",
        });
      }
    },
    async fetchCreditApplication(id) {
      try {
        this.setStatus({
          form: this.formName,
          status: "loading",
        });

        const response = await axios.get(
            `/credit-dashboard/applications/${id}`,
            {
              params: {
                relations: ["resaleCertImage", "account", "actionedBy"]
              }
            }
        );

        const data = response.data;
        data.additional_addresses = JSON.parse(data.additional_addresses);

        this.setFields({
          form: this.formName,
          fields: {
            ...data,
            agreeToTermsAndConditions: true,
          },
        });

        if (data.resale_cert_image) {
          this.images = [data.resale_cert_image];
        }

        if (data.account) {
          this.account_id = data.account.id;
          this.account_name = data.account.accountName;
        }

        if (data.actioned_by) {
          this.actioned_by_name = data.actioned_by.name;
        }

        this.status = data.status;
        this.updated_at = data.updated_at;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }

        throw err;
      } finally {
        this.setStatus({
          form: this.formName,
          status: "idle",
        });
      }
    },
    isImage(url) {
      return isImage(url);
    },
  },
  computed: {
    ...mapGetters("forms", ["getStatus"]),
    isLoading() {
      return this.getStatus({form: this.formName}) === "loading";
    },
  },
};
</script>
